<template>
  <div class="tw-flex tw-flex-col tw-gap-2 tw-rounded-lg">
    <div class="tw-flex tw-flex-col">
      <c-button
        v-if="chats.length > SHOW_DEFAULT_CHAT_AMOUNT"
        :icon="
          !showAllChats
            ? 'fa-duotone fa-message-arrow-up'
            : 'fa-duotone fa-message-arrow-down'
        "
        outline
        size="md"
        :label="
          !showAllChats
            ? `Toon alle chats (${props.chats?.length || 0})`
            : 'Toon minder chats'
        "
        class="tw-mb-2 tw-self-center"
        @click="showAllChats = !showAllChats"
      />

      <ul class="c-chat-screen" v-auto-animate>
        <li v-if="!formattedChats.length">
          <span
            class="tw-flex tw-px-4 tw-py-2 tw-text-sm tw-italic tw-text-slate-500"
            >Er zijn nog geen reacties...</span
          >
        </li>

        <li v-for="chat in formattedChats" :key="`${name}-${stamp}-${text}`">
          <c-chat-message :chat />
        </li>
      </ul>
    </div>

    <c-form v-if="!readonly" class="tw-flex tw-gap-x-2" @submit="addChat">
      <c-input
        v-model="message"
        dense
        class="tw-line-height-2 tw-flex-1 tw-p-0"
        bg-color="white"
        :placeholder="placeholderText"
        :rules="[required]"
        lazy-rules
      />

      <c-button
        :disabled="sending"
        type="submit"
        icon="fas fa-send"
        :disable="!message.length"
      />
    </c-form>

    <notification-message
      class="tw-my-4"
      :notification-key="notificationMessageContainer"
    />
  </div>
</template>

<script setup>
import { computed, ref } from "vue";
import useHelpers from "composables/useHelpers.js";
import { useNotifyStore } from "store/modules/notifyStore.js";
import NotificationMessage from "components/global/NotificationMessage.vue";
import { required } from "common/validationRules.js";

// Notify store
const notifyStore = useNotifyStore();
const { setNotification } = notifyStore;

// Helpers
const { user } = useHelpers();

const props = defineProps({
  chats: {
    type: Array,
    required: true,
    default: () => [],
  },
  readonly: {
    type: Boolean,
    default: false,
  },
  callbackFunction: {
    type: Function,
    default: () => {},
  },
  extraParameters: {
    type: Object,
    default: () => ({}),
  },
});

const notificationMessageContainer = computed(() => {
  return Math.random().toString(36).substring(2, 15);
});

const generateChatTimestamp = (isoDate) => {
  const dateObj = new Date(isoDate);
  const dateOptions = {
    weekday: "short",
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  };

  return new Intl.DateTimeFormat("nl-NL", dateOptions).format(dateObj);
};

const SHOW_DEFAULT_CHAT_AMOUNT = 3;
const showAllChats = ref(false);
const mapChats = ({
  user_display_name,
  message,
  created_at,
  user_id,
  isSender = null,
  systemMessage = false,
}) => {
  return {
    name: user_display_name,
    text: [message],
    stamp: generateChatTimestamp(created_at),
    sent: isSender ?? user.id === user_id,
    avatar: "/images/empty-profile-pic.webp",
    systemMessage,
  };
};

const formattedChats = computed(() => {
  const { chats = [] } = props || {};

  if (!chats.length) {
    return [];
  }

  const showAmount = showAllChats.value
    ? props.chats.length
    : SHOW_DEFAULT_CHAT_AMOUNT;

  return props.chats.map(mapChats).slice(-showAmount).reverse();
});

const sending = ref(false);
const message = ref("");

const addChat = async () => {
  if (props.readonly) return;

  try {
    sending.value = true;

    const chatData = {
      ...props.extraParameters,
      message: message.value,
    };

    await props.callbackFunction(chatData);

    message.value = "";

    setNotification({
      key: notificationMessageContainer.value,
      message: "Reactie geplaatst",
      type: "success",
    });
  } catch (error) {
    console.error(error);

    setNotification({
      key: notificationMessageContainer.value,
      message:
        error?.response?.data?.message ||
        "Er is iets fout gegaan bij het plaatsen van je reactie, probeer het nog eens",
      type: "error",
    });
  } finally {
    sending.value = false;
  }
};

const placeholderText = computed(() => {
  return !formattedChats.value.length
    ? "Typ hier de eerste reactie bij dit cluster…"
    : "Typ een bericht...";
});
</script>

<style>
.q-message-text--sent {
  @apply tw-bg-blue-100;

  &::before,
  &:last-child::before {
    @apply tw-border-b-blue-100;
  }
}

.q-message-stamp {
  font-size: 0.8rem;
  color: #525252;
  text-align: right;
}

.c-chat-screen {
  @apply tw-flex tw-flex-col-reverse tw-gap-2 tw-overflow-hidden tw-overscroll-none tw-px-0 tw-py-2 sm:tw-overscroll-contain;
}
</style>
